import React, { useState } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import { Box, Typography, Divider, IconButton } from "@material-ui/core";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { submitIntakeLinkDocument } from "store/slices/client";
import { showToast } from "store/slices/toast";
import DeleteIcon from "@material-ui/icons/Delete";
import snakecaseKeys from "snakecase-keys";
import moment from "moment";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const CustomFileUploadBox = ({ label, file, initialFileName, onFileSelect }) => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef(null);
  return (
    <Box display="flex" alignItems="center" p={1.5} bgcolor="#F9FAFB" border="1px solid #E5E7EB" borderRadius={6}>
      <i className="fas fa-file-pdf" style={{ fontSize: 18, color: "#EF4444", marginRight: 8 }} />
      <Box flex={1} textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden" fontSize={13} color="#374151">
        {file ? file.name : initialFileName ? initialFileName : t("text.no_file_selected")}
      </Box>
      <AppButton
        label={file || initialFileName ? t("button.change") : label}
        mode="outlined"
        rounder
        style={{ fontSize: 11, textTransform: "none", height: 28, marginLeft: 8 }}
        onClick={() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
          }
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) onFileSelect(f);
        }}
      />
    </Box>
  );
};

const IntakeLinkDocuments = ({ clientData, token, onNextStep }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [deletedDocIds, setDeletedDocIds] = useState([]);

  const initialDocuments = (clientData?.clientDocumentResponses || []).map(doc => {
    const detail = doc.clientDocumentDetailResponse?.[0];
    return {
      id: doc.id,
      name: doc.fileName || "",
      category: doc.fileCategory || "",
      reviewDueDate: detail?.reviewDueDate ? moment(detail?.reviewDueDate).format("YYYY/MM/DD") : "",
      file: null,
      existingFileName: detail?.fileS3Name || ""
    };
  });

  const initialValues = {
    clientDocuments: initialDocuments.length > 0 ? initialDocuments : [],
  };

  const validationSchema = Yup.object().shape({
    clientDocuments: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
        category: Yup.string().required(t("validation.required")),
      })
    ),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    const payload = {
      intakeLinkToken: token,
      clientDocuments: values.clientDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.category,
        reviewDueDate: doc.reviewDueDate || null,
      })),
      deletedDocumentIds: deletedDocIds,
    };

    const formData = new FormData();
    const snakeCasePayload = snakecaseKeys(payload);
    console.log(' >>> snakeCasePayload: ', snakeCasePayload);
    
    formData.append("content-data", new Blob([JSON.stringify(snakeCasePayload)], { type: "application/json" }));
    
    values.clientDocuments.forEach((doc) => {
      if (doc.file) {
        formData.append("clientDocumentFiles", doc.file);
      } else {
        formData.append("clientDocumentFiles", new Blob([]));
      }
    });

    try {
      const response = await dispatch(submitIntakeLinkDocument(formData));
      if (response && response.payload) {
        onNextStep(true);
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit Documents", type: "error" }));
    }
    setLoading(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, setFieldValue, values }) => (
        <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.documents")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />

          <Box mb={3}>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 10 }}>{t("label.supporting_documents")}</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 15 }}>
              {t("message.upload_relevant_documents")}
            </Typography>
            
            <FieldArray name="clientDocuments">
              {({ push, remove }) => (
                <div>
                  {values.clientDocuments && values.clientDocuments.map((doc, index) => (
                    <Box key={index} p={2} mb={2} border="1px solid #E5E7EB" borderRadius={8} position="relative" bgcolor="#F9FAFB">
                      <IconButton size="small" style={{ position: "absolute", top: 5, right: 5, color: AppColor.red }} onClick={() => {
                        if (doc.id) {
                          setDeletedDocIds(prev => [...prev, doc.id]);
                        }
                        remove(index);
                      }}>
                        <DeleteIcon />
                      </IconButton>
                      <div className="row mt-4">
                        <div className="col-sm-12 col-md-12 mb-3">
                          <CustomFileUploadBox
                            label={t("button.select_file")}
                            onFileSelect={(f) => {
                              setFieldValue(`clientDocuments.${index}.file`, f);
                              if (!values.clientDocuments[index].name && f) {
                                setFieldValue(`clientDocuments.${index}.name`, f.name);
                              }
                            }}
                            file={doc.file}
                            initialFileName={doc.existingFileName}
                          />
                        </div>
                        <div className="col-sm-12 col-md-6">
                          <Field component={AppInput} name={`clientDocuments.${index}.name`} label={t("label.document_name", "Document Name")} required style={{ marginBottom: 10 }} />
                        </div>
                        <div className="col-sm-12 col-md-6">
                          <Field
                            component={SelectField}
                            name={`clientDocuments.${index}.category`}
                            label={t("label.category")}
                            required
                            options={[
                              { value: "NDIS", label: t("label.ndis_plan") },
                              { value: "CLINICAL", label: t("label.clinical_medical_report") },
                              { value: "LEGAL", label: t("label.legal_document") },
                              { value: "CONSENT", label: t("label.consent_form") },
                              { value: "COMPLIANCE", label: t("label.compliance_id") },
                              { value: "OTHER", label: t("label.other") }
                            ]}
                          />
                        </div>
                        <div className="col-sm-12 col-md-6">
                          <Field
                            component={CustomDatePicker}
                            label={t("label.review_due_date")}
                            name={`clientDocuments.${index}.reviewDueDate`}
                            type="text"
                            formatDateJob={true}
                            custom={{ disablePast: true }}
                            classInput="date-picker-container-custom"
                          />
                        </div>
                      </div>
                    </Box>
                  ))}
                  <AppButton
                    label={t("button.add_document")}
                    mode="outlined"
                    buttonColor={AppColor.mainColor}
                    startIcon={<i className="fas fa-plus" style={{ fontSize: 14 }} />}
                    style={{ textTransform: "none", backgroundColor: "white", marginTop: 10 }}
                    onClick={() => push({ name: "", file: null, category: "OTHER" })}
                  />
                </div>
              )}
            </FieldArray>
          </Box>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.saveAndNext")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={!isValid || loading}
              style={{ width: "150px" }}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default IntakeLinkDocuments;
